import React, { useContext, createContext, useState, useEffect, useCallback, useMemo } from "react";

// Theme, shared by both interfaces.
//
// Three modes, not two: light, dark, and system — the last following the OS setting and
// changing with it, without a reload.
//
// The boolean API is kept exactly as it was. Every v1 surface (Navbar, Dashboard, Help,
// LandingPage, BugReportForm and others) reads `isDarkMode` and calls
// `setIsDarkMode(prev => !prev)`, so both the value and the functional-updater form still
// work; toggling from v1 simply resolves to an explicit light or dark, which is what
// someone flipping a two-state switch means.
//
// `isDarkMode` also keeps being mirrored to localStorage under its original key. Nothing
// outside this file reads it today, but it is the format every existing browser already
// has stored, and it is what the migration below reads.

const ThemeContext = createContext();

const MODES = ["light", "dark", "system"];
const MODE_KEY = "themeMode";
const LEGACY_KEY = "isDarkMode";

const QUERY = "(prefers-color-scheme: dark)";

// Guarded: matchMedia is missing in some test environments, and localStorage throws
// outright in a private window rather than returning null.
const systemPrefersDark = () => {
  try { return window.matchMedia && window.matchMedia(QUERY).matches; }
  catch { return false; }
};

const readInitialMode = () => {
  try {
    const saved = localStorage.getItem(MODE_KEY);
    if (MODES.includes(saved)) return saved;

    // Migration. v1 wrote this key on mount, so essentially every existing browser has
    // one — which means existing users keep exactly the theme they had, and only a
    // genuinely new browser falls through to following the system.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy !== null) return JSON.parse(legacy) ? "dark" : "light";
  } catch { /* private mode */ }
  return "system";
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(readInitialMode);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Watched always, not only while mode is "system": the listener is cheap, and
  // subscribing conditionally means the first change after switching to System is missed
  // until something else re-renders.
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(QUERY); } catch { return undefined; }
    if (!mq) return undefined;

    const onChange = (e) => setSystemDark(e.matches);
    // addListener is the Safari < 14 spelling; still present where addEventListener is not.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  const isDarkMode = mode === "system" ? systemDark : mode === "dark";

  useEffect(() => {
    try {
      localStorage.setItem(MODE_KEY, mode);
      localStorage.setItem(LEGACY_KEY, JSON.stringify(isDarkMode));
    } catch { /* private mode */ }
  }, [mode, isDarkMode]);

  // Tells the browser which palette its own widgets should use — scrollbars, native
  // selects, form controls, the space behind a rubber-band scroll. Without it a dark page
  // still shows light scrollbars.
  useEffect(() => {
    document.documentElement.style.colorScheme = isDarkMode ? "dark" : "light";
  }, [isDarkMode]);

  // The v1 signature: accepts a boolean or an updater taking the current boolean.
  const setIsDarkMode = useCallback((next) => {
    setMode((current) => {
      const currentlyDark = current === "system" ? systemPrefersDark() : current === "dark";
      const wanted = typeof next === "function" ? next(currentlyDark) : next;
      return wanted ? "dark" : "light";
    });
  }, []);

  const value = useMemo(
    () => ({ isDarkMode, setIsDarkMode, mode, setMode, systemDark }),
    [isDarkMode, setIsDarkMode, mode, systemDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
